import {useCallback, useEffect, useRef, useState} from "react";
import LoadingScreen from "./components/LoadingScreen";
import HomePage from "./homepage/HomePage";
import NewsDetailPage from "./news/NewsDetailPage";
import LegalPage from "./legal/LegalPage";

const CDN_BASE_URL = (process.env.REACT_APP_CDN_BASE_URL || process.env.PUBLIC_URL || "").replace(/\/$/, "");
const buildMediaUrl = function (path) {
    if (/^https?:\/\//.test(path)) return path;
    if (!CDN_BASE_URL) return path;
    return `${CDN_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
};
const BG_MUSIC_URL = buildMediaUrl(process.env.REACT_APP_BG_MUSIC_URL || "/audio/bg-music.mp3");

function getRoute() {
    const hash = window.location.hash || "";
    const newsMatch = hash.match(/^#\/newsdetail\/([^/?#]+)/);
    const legalMatch = hash.match(/^#\/(user-agreement|privacy-policy|contact-us)/);

    if (newsMatch) {
        return {
            name: "newsDetail",
            id: decodeURIComponent(newsMatch[1])
        };
    }

    if (legalMatch) {
        return {
            name: "legal",
            type: legalMatch[1]
        };
    }

    return {name: "home"};
}

export default function App() {
    const [route, setRoute] = useState(getRoute);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isMusicOn, setIsMusicOn] = useState(false);
    const bgMusicRef = useRef(null);

    useEffect(() => {
        const handleHashChange = function () {
            const nextRoute = getRoute();
            setRoute(nextRoute);

            if (nextRoute.name !== "home") {
                window.scrollTo(0, 0);
            }
        };

        window.addEventListener("hashchange", handleHashChange);

        return () => {
            window.removeEventListener("hashchange", handleHashChange);
        };
    }, []);

    useEffect(() => {
        let current = 0;
        const timerId = window.setInterval(function () {
            current += Math.max(1, Math.round((100 - current) / 8));
            if (current >= 100) {
                current = 100;
                setLoadingProgress(current);
                window.clearInterval(timerId);
                window.setTimeout(function () {
                    setIsLoading(false);
                }, 1000);
                return;
            }
            setLoadingProgress(current);
        }, 70);

        return () => {
            window.clearInterval(timerId);
        };
    }, []);

    useEffect(() => {
        const bgMusic = bgMusicRef.current;
        if (isLoading || !bgMusic) return undefined;

        bgMusic.volume = 0.45;
        const syncMusicState = function () {
            setIsMusicOn(!bgMusic.paused);
        };
        const tryPlayMusic = function () {
            bgMusic.play().then(function () {
                setIsMusicOn(true);
                window.removeEventListener("click", tryPlayMusic);
                window.removeEventListener("keydown", tryPlayMusic);
            }).catch(function () {
                setIsMusicOn(false);
            });
        };

        bgMusic.addEventListener("play", syncMusicState);
        bgMusic.addEventListener("pause", syncMusicState);
        tryPlayMusic();
        window.addEventListener("click", tryPlayMusic, {once: true});
        window.addEventListener("keydown", tryPlayMusic, {once: true});

        return () => {
            bgMusic.removeEventListener("play", syncMusicState);
            bgMusic.removeEventListener("pause", syncMusicState);
            window.removeEventListener("click", tryPlayMusic);
            window.removeEventListener("keydown", tryPlayMusic);
        };
    }, [isLoading]);

    const handleMusicToggle = useCallback(function () {
        const bgMusic = bgMusicRef.current;
        if (!bgMusic) return;

        if (bgMusic.paused) {
            bgMusic.play().catch(function () {
                setIsMusicOn(false);
            });
        } else {
            bgMusic.pause();
        }
    }, []);

    const handleMusicPause = useCallback(function () {
        const bgMusic = bgMusicRef.current;
        const wasPlaying = Boolean(bgMusic && !bgMusic.paused);
        if (wasPlaying) bgMusic.pause();
        return wasPlaying;
    }, []);

    const handleMusicPlay = useCallback(function () {
        const bgMusic = bgMusicRef.current;
        if (!bgMusic || !bgMusic.paused) return;

        bgMusic.play().catch(function () {
            setIsMusicOn(false);
        });
    }, []);

    if (isLoading) {
        return <LoadingScreen progress={loadingProgress} />;
    }

    const musicProps = {
        isMusicOn,
        onMusicToggle: handleMusicToggle
    };
    let page = <HomePage {...musicProps} onMusicPause={handleMusicPause} onMusicPlay={handleMusicPlay} />;

    if (route.name === "newsDetail") {
        page = <NewsDetailPage id={route.id} {...musicProps} />;
    }

    if (route.name === "legal") {
        page = <LegalPage type={route.type} {...musicProps} />;
    }

    return (
        <>
            {page}
            <audio ref={bgMusicRef} id="bgMusic" src={BG_MUSIC_URL} loop preload="auto"></audio>
            <div className="development-notice" aria-hidden="true">*游戏研发中，非最终品质。</div>
        </>
    );
}
